import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { OptionBadge, OptionRule, OptionKind, OPTION } from "./OptionBadge";

interface Side { kind: OptionKind; title: string; tagline: string; image: string; bullets: [string, string][]; ctas: { to: string; label: string; primary?: boolean }[] }

const SIDES: Side[] = [
  {
    kind: "cellar", title: "Cellar Plan", tagline: "Build an account balance. Spend it on whatever you like.", image: "/brand/club/wooden-case.png",
    bullets: [["What you get", "Credit in your account, to spend on any wine, tasting or gift — you choose every bottle."], ["How you pay", "A regular Direct Debit of any amount (weekly, monthly or quarterly), one-off top-ups by card or bank, or auto top-up when you run low."], ["Who it's for", "Members who like to browse, pick and order in their own time."]],
    ctas: [{ to: "/cellar-plan", label: "Set up a Cellar Plan", primary: true }, { to: "/cellar?tab=topup", label: "Add credit" }],
  },
  {
    kind: "club", title: "Wine Club", tagline: "Twelve bottles chosen for you, delivered every twelve weeks.", image: "/brand/club/subscription-box.png",
    bullets: [["What you get", "A curated case from the Hourlier family — Discovery Plan, Pierre's Selection or Magnificent Dozen — shaped around your tastes."], ["How you pay", "From £60 a month by Direct Debit, per case from your account balance, or by card."], ["Who it's for", "Members who want the surprise of new wines without having to choose."]],
    ctas: [{ to: "/club", label: "See the Wine Club plans", primary: true }],
  },
];

function OptionCard({ s, delay }: { s: Side; delay: number }) {
  const o = OPTION[s.kind];
  return (
    <div className="relative flex flex-col p-8 lg:p-10 rise" style={{ background: o.bg, animationDelay: `${delay}ms` }} data-testid={`option-card-${s.kind}`}>
      <div className="absolute inset-x-0 top-0 h-1.5" style={{ background: o.color }} />
      <div className="flex items-start justify-between gap-6">
        <div>
          <OptionBadge kind={s.kind} text={s.kind === "cellar" ? "Option 1" : "Option 2"} />
          <h3 className="mt-4 font-serif text-3xl sm:text-4xl" style={{ color: o.dark }}>{s.title}</h3>
          <p className="mt-2 max-w-sm text-sm text-neutral-700">{s.tagline}</p>
        </div>
        <img src={s.image} alt="" className="h-24 w-24 shrink-0 object-contain sm:h-32 sm:w-32" />
      </div>
      <div className="mt-5"><OptionRule kind={s.kind} /></div>
      <dl className="mt-6 flex-1 space-y-4">
        {s.bullets.map(([k, v]) => (
          <div key={k} className="grid gap-1 sm:grid-cols-[120px_1fr] sm:gap-4">
            <dt className="eyebrow" style={{ color: o.dark }}>{k}</dt>
            <dd className="text-sm leading-relaxed text-neutral-800">{v}</dd>
          </div>
        ))}
      </dl>
      <div className="mt-8 flex flex-wrap gap-3">
        {s.ctas.map((c) => (
          <Link key={c.to} to={c.to} className={`group inline-flex items-center gap-2 border px-5 py-3 text-xs uppercase tracking-[0.18em] transition-colors duration-200 ${c.primary ? "text-[var(--paper)]" : "bg-transparent hover:text-[var(--paper)]"}`}
            style={c.primary ? { background: o.dark, borderColor: o.dark } : { color: o.dark, borderColor: o.dark }}
            onMouseEnter={(e) => { if (!c.primary) e.currentTarget.style.background = o.dark; }} onMouseLeave={(e) => { if (!c.primary) e.currentTarget.style.background = "transparent"; }}
            data-testid={`option-cta-${s.kind}-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            {c.label} <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </Link>
        ))}
      </div>
    </div>
  );
}

export function OptionExplainer() {
  return (
    <section data-testid="option-explainer">
      <div className="eyebrow">Two ways to use your cellar</div>
      <h2 className="mt-2 font-serif text-2xl sm:text-3xl">Which one is which?</h2>
      <p className="mt-2 max-w-2xl text-sm text-neutral-600">They're two separate things and each has its own colour throughout the app — <span className="font-medium" style={{ color: "var(--cellar-dark)" }}>tan for your Cellar Plan and account balance</span>, <span className="font-medium" style={{ color: "var(--club-dark)" }}>burgundy for the Wine Club</span>. Run one, the other, or both.</p>
      <div className="mt-8 grid gap-px border border-border bg-border md:grid-cols-2">
        {SIDES.map((s, i) => <OptionCard key={s.kind} s={s} delay={i * 80} />)}
      </div>
    </section>
  );
}

export function HowItWorksStrip() {
  return (
    <div className="border border-border" data-testid="how-it-works">
      <div className="flex items-center gap-4 border-b border-border bg-[var(--parchment)] px-5 py-3">
        <img src="/brand/club/subscription-guide.png" alt="" className="h-12 w-12 object-contain" />
        <div>
          <div className="eyebrow">How it works</div>
          <div className="text-sm">One account, two ways to use it — run one or both.</div>
        </div>
      </div>
      <div className="grid gap-px bg-border sm:grid-cols-2">
        {SIDES.map((s) => (
          <div key={s.kind} className="p-5" style={{ background: OPTION[s.kind].bg }} data-testid={`how-it-works-${s.kind}`}>
            <OptionBadge kind={s.kind} />
            <div className="mt-2 font-serif text-xl" style={{ color: OPTION[s.kind].dark }}>{s.title}</div>
            <p className="mt-1 text-xs leading-relaxed text-neutral-700">{s.tagline} {s.bullets[1][1]}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
